import { FileArrowDownIcon } from '@phosphor-icons/react';
import { PlusCircleIcon } from 'lucide-react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useFilters } from '@/hooks';
import { GenericTable, Spinner } from '@/modules/common';
import { useExportAuditEntries } from '@/modules/events/api';
import { AuditReportType } from '@/modules/events/constants';
import { CreateGcrDialog } from '@/modules/gcrs/components';

import { AddEditModelDialog } from './components/add-edit-model-dialog';
import { AddSizeDialog } from './components/add-size-dialog';
import { ModelsFilters } from './components/models-filters';
import { filterData } from './helpers/filter';
import { useModels } from './hooks/use-models';
import { ModelFiltersType } from './types/filters';
import { ModelDataType } from './types/model';

type DraggableModelDataType = ModelDataType & {
  modelIndex: number;
};

export function ModelsTab() {
  const [selectedModel, setSelectedModel] = useState<ModelDataType | null>(
    null
  );
  const [addModelDialogOpen, setAddModelDialogOpen] = useState(false);
  const [addSizeForModelDialogOpen, setAddSizeForModelDialogOpen] =
    useState(false);

  const params = useParams<{ id: string }>();
  const { getValues } = useFormContext();

  const { data, isFetching, columns, subColumns, options, form, gcr } =
    useModels();
  const { isExportingAudit, exportAudit } = useExportAuditEntries({
    eventId: params.id,
    eventName: getValues('name'),
  });
  const { filters, filtersChange } = useFilters<ModelFiltersType>({
    search: '',
    types: [],
    genders: [],
    sizes: [],
  });

  if (isFetching) return <Spinner />;

  const filteredData = filterData(data, filters);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Models Table</h3>

        <div className="flex items-center gap-4">
          <Button
            disabled={isFetching}
            data-testid="add-model-button"
            onClick={() => setAddModelDialogOpen(true)}
            startIcon={<PlusCircleIcon size={16} />}
          >
            Add model
          </Button>

          <Button
            variant="outline"
            data-testid="export-audit-button"
            onClick={() => exportAudit(AuditReportType.Event)}
            startIcon={<FileArrowDownIcon size={16} />}
            isLoading={isExportingAudit}
          >
            Export audit
          </Button>
        </div>
      </div>

      <GenericTable
        data={filteredData}
        columns={columns}
        subColumns={subColumns}
        subKey="sizes"
        emptyCellClassName="h-20"
        tableCellClassName="px-3 py-4"
        tableHeadCellClassName="p-3"
        tableRowClassName="hover:bg-transparent"
        initialState={{ pagination: { pageIndex: 0, pageSize: 100 } }}
        emptyTitle="No models yet"
        emptyDescription="Start building your models."
        sizeStyleProperty="minWidth"
        emptyButton={
          <Button
            disabled={isFetching}
            data-testid="add-model-button"
            onClick={() => setAddModelDialogOpen(true)}
            startIcon={<PlusCircleIcon size={16} />}
          >
            Add model
          </Button>
        }
        renderSubFooter={(model: ModelDataType) => (
          <Button
            variant="ghost"
            startIcon={<PlusCircleIcon size={16} />}
            onClick={() => {
              setSelectedModel(model);
              setAddSizeForModelDialogOpen(true);
            }}
          >
            Add size
          </Button>
        )}
        onClick={(item: ModelDataType) => {
          setSelectedModel(item);
          setAddModelDialogOpen(true);
        }}
        disabledColumnsIdsToClick={[
          'dragHandle',
          'expander',
          'name',
          'sizes',
          'quantity',
          'reservedQuantity',
          'position',
          'gcrFile',
          'actions',
        ]}
        countable
        countLabel="All models:"
        isFiltered
        filteredKey="isVisible"
        rowDraggable
        getRowDragId={(model: ModelDataType) => model.modelId}
        onRowDragEnd={(
          draggedModel: ModelDataType,
          targetModel: ModelDataType
        ) => {
          const draggedIndex = (draggedModel as DraggableModelDataType).modelIndex;
          const targetIndex = (targetModel as DraggableModelDataType).modelIndex;
          form.move(draggedIndex, targetIndex);
        }}
      >
        <ModelsFilters
          options={{
            types: options.typeOptions,
            genders: options.genderOptions,
            sizes: options.sizeOptions,
          }}
          filters={filters}
          onFilters={filtersChange}
        />
      </GenericTable>

      {gcr.createGcrDetails && (
        <CreateGcrDialog
          isOpen
          onSuccess={() => gcr.refetchGcrs()}
          onOpenChange={() => gcr.setCreateGcrDetails(null)}
          {...gcr.createGcrDetails}
        />
      )}

      <AddEditModelDialog
        isOpen={addModelDialogOpen}
        onOpenChange={(open) => {
          setSelectedModel(null);
          setAddModelDialogOpen(open);
        }}
        modelOptions={options.modelOptions}
        sizeOptions={options.sizeOptions}
        selectedModel={selectedModel}
        onAdd={(model) => {
          const existModelIndex = data.findIndex(
            (item) => item.modelId === model.modelId
          );

          existModelIndex === -1
            ? form.append(model)
            : form.update(existModelIndex, {
                ...data[existModelIndex],
                sizes: [...data[existModelIndex].sizes, ...model.sizes],
              });
        }}
        onEdit={(model) => {
          const index = data.findIndex(
            (field) => field.modelId === selectedModel?.modelId
          );

          if (index !== -1) form.update(index, model);
        }}
      />

      <AddSizeDialog
        isOpen={addSizeForModelDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedModel(null);
            setAddSizeForModelDialogOpen(false);
          }
        }}
        sizeOptions={options.sizeOptions}
        existingSizeIds={selectedModel?.sizes.map((s) => s.sizeId) ?? []}
        onAdd={(newSize) => {
          if (!selectedModel) {
            return;
          }

          const index = data.findIndex(
            (item) => item.modelId === selectedModel.modelId
          );
          if (index !== -1) {
            form.update(index, {
              ...data[index],
              sizes: [...data[index].sizes, newSize],
            });
          }
        }}
      />
    </div>
  );
}
